import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { DataTable } from '@components/tables'
import { DialogTitle } from '@components/titles'
import {
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { PrepRoom as PrepRoomType, RoomType } from '@services/api'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import { findAllRooms, removePrepRoom } from '@state/thunks/roomThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { removeSites } from '@state/reducers/siteReducer'
import { PrepRoomForm } from './PrepRoomForm'
import { EditIcon } from 'lucide-react'

export default function PrepRoom() {
  const dispatch = useDispatch()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [editingPrepRoom, setEditingPrepRoom] = useState<
    PrepRoomType | undefined
  >(undefined)

  const { loading, prepRooms } = useSelector(({ room, loading }) => ({
    prepRooms: room.rooms.filter((room) => room.type === RoomType.PrepRoom),
    loading:
      loading[Actions.UPDATE_PREP_ROOM] ||
      loading[Actions.CREATE_PREP_ROOM] ||
      loading[Actions.ROOM_FIND_ALL],
  }))
  const columns: GridColumns = [
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'label', headerName: 'Label', flex: 1 },
    {
      field: 'examRoom',
      headerName: 'Exam-room',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as any)?.label
      },
    },
    {
      field: 'wheelchairCompatible',
      headerName: 'Wheelchair',
      flex: 1,
      type: 'boolean',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => {
              setEditingPrepRoom(params.row)
              setOpenAddDialog(true)
            }}
          >
            <EditIcon />
          </IconButton>
        )
      },
    },
  ]

  const onRefresh = useCallback(() => {
    dispatch(findAllRooms())
    dispatch(findAllSites())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }
  const _onDeleteConfirm = () => {
    dispatch(removePrepRoom({ id: selectedRows }))
    setIsDeleteDialogOpen(false)
  }

  return (
    <Container maxWidth={false} style={{ height: '93%', padding: '0px 24px' }}>
      <Paper
        elevation={2}
        style={{ width: '100%', borderRadius: '0px 15px 15px 15px' }}
      >
        <DataTable
          rows={prepRooms}
          columns={columns}
          loading={loading}
          toolBarProps={{
            title: 'rooms',
            icon: 'fab fa-uikit',
            onAdd: () => {
              setEditingPrepRoom(undefined)
              setOpenAddDialog(true)
            },
            onRefresh,
            onDelete: () => {
              selectedRows.length
                ? setIsDeleteDialogOpen(true)
                : alert('please select at least one room')
            },
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>

      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
        TransitionProps={{
          onExited: () => setEditingPrepRoom(undefined),
        }}
      >
        <DialogTitle
          title={editingPrepRoom ? 'editPrepRoom' : 'addPrepRoom'}
          onClose={() => setOpenAddDialog(false)}
          format
        />
        <DialogContent>
          <PrepRoomForm
            key={editingPrepRoom?.id ?? 'new'}
            onClose={() => setOpenAddDialog(false)}
            row={editingPrepRoom}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Etes-vous sur de vouloir supprimer les salles selectionnees ?"
      />
    </Container>
  )
}
